// From https://github.com/facebook/react/issues/5465#issuecomment-157888325

export interface CancelablePromise<T> {
  promise: Promise<T>;
  cancel: () => void;
}

/**
 * Make promise cancelable by adding `.cancel` method to promise
 *
 * Once a cancelable promise has been canceled, it's will fail with `{isCanceled: true}` regardless of
 * whether or not the wrapped promise succeeds or fails.
 *
 * Canceled promises should only be used in the method in which they are created. This prevents the error
 * prone scenario where two modules A and B have the same cancelable promise and A cancels while B would
 * still like to continue with the promise chain.
 *
 * Sample usage from https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html:
 * ```
 * const cancelablePromise = makeCancelable(
 *   new Promise(r => component.setState({...}}))
 * );
 *
 * cancelablePromise
 *   .promise
 *   .then(() => console.log('resolved'))
 *   .catch((reason) => console.log('isCanceled', reason.isCanceled));
 *
 * cancelablePromise.cancel(); // Cancel the promise
 * ```
 *
 * @param  {Promise} promise Promise to be made cancelable
 * @return {Promise}         Cancelable promise
 */
export default function makeCancelable<T>(promise: Promise<T>): CancelablePromise<T> {
  let hasCanceled_ = false;

  const wrappedPromise: Promise<T> = new Promise((resolve, reject) => {
    promise
      .then((val) => {
        if (hasCanceled_) {
          reject({isCanceled: true}); // oxlint-disable-line prefer-promise-reject-errors
        } else {
          resolve(val);
        }
      })
      .catch((error) => {
        if (hasCanceled_) {
          reject({isCanceled: true}); // oxlint-disable-line prefer-promise-reject-errors
        } else {
          reject(error);
        }
      });
  });

  return {
    promise: wrappedPromise,
    cancel(): void {
      hasCanceled_ = true;
    },
  };
}
